import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { Collapse, useMediaQuery } from '@mui/material'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton, brandPrimaryButton } from '../../_helpers/brandButtons'
import { brandHeading, brandText } from '@/utils/brand-typography'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import SellOutlineIcon from '@assets/images/icons/v2/sell-outline.svg'
import { dateConstants } from '@/modules/base/util/constants'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import getPlanDescriptions from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/getPlanDescriptions'
import { ISubscriptionSelectionItem } from '../../types'
import { getPlanPrice } from '../../_helpers/getPlanPrice'

// a plan compared with the plan an existing customer already has
export const PLAN_STATUS = Object.freeze({
  // no plan from the shop yet, or a higher plan than the current one
  available: 'available',
  current: 'current',
  // lower than the current plan - a downgrade goes through the support
  locked: 'locked',
})
export type PlanStatus = typeof PLAN_STATUS[keyof typeof PLAN_STATUS]

// The rows a plan takes in the grid of PlanStep: the label above the card, then the sections of the card.
// Every card places its sections in the same rows, so they line up across the cards (an empty section,
// e.g. no promotion, leaves its row empty instead of moving everything below it up).
const CARD_ROWS = ['category', 'claim', 'description', 'price', 'discount', 'action', 'features'] as const
const row = (name: typeof CARD_ROWS[number]) => ({ gridRow: CARD_ROWS.indexOf(name) + 1 })

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'grid',
    gridRow: `span ${CARD_ROWS.length + 1}`,
    gridTemplateRows: 'subgrid',
    rowGap: 0,
    minWidth: 0,
    borderRadius: theme.borderRadiusExtraExtraLarge,
    // a single column (tablet, phone): the cards are stacked, nothing to line up
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      gridRow: 'auto',
    },
  },
  // a label above the card, e.g. "Empfehlung" or "Dein aktueller Plan"
  withBand: {
    padding: '0 4px 4px',
    boxShadow: theme.shadowCard,
  },
  recommended: {
    background: `linear-gradient(117deg, ${theme.palette.brand.main} 33.12%, #AFC2E9 115.29%)`,
  },
  current: {
    background: theme.palette.brand.light,
    border: `1px solid ${theme.palette.brand.divider}`,
  },
  band: {
    gridRow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '6px 0',
    fontSize: theme.brandTypography.text.small.fontSize,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    '& svg': {
      width: 18,
      height: 18,
    },
  },
  bandRecommended: {
    color: theme.palette.primary.contrastText,
  },
  bandCurrent: {
    color: theme.palette.brand.main,
  },
  // a card without a label above it leaves the label row empty and starts with the cards below the labels
  withoutBand: {
    boxShadow: theme.shadowCard,
    borderRadius: theme.borderRadiusExtraLarge,
  },
  card: {
    gridRow: `2 / span ${CARD_ROWS.length}`,
    display: 'grid',
    gridTemplateRows: 'subgrid',
    rowGap: 0,
    alignContent: 'start',
    padding: 24,
    border: `1px solid ${theme.palette.brand.divider}`,
    borderRadius: theme.borderRadiusExtraLarge,
    background: theme.palette.background.paper,
    color: theme.palette.brand.text.primary,
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  // the same border width as the cards without a label, so their content lines up to the pixel
  cardInBand: {
    borderColor: 'transparent',
  },
  // the sections of a card, each in its row - the spacing sits on top of each section, so an empty
  // row (no promotion) adds no gap
  rowCategory: row('category'),
  rowClaim: {
    ...row('claim'),
    marginTop: 6,
  },
  rowDescription: {
    ...row('description'),
    marginTop: 6,
  },
  rowPrice: {
    ...row('price'),
    marginTop: 16,
  },
  rowDiscount: row('discount'),
  rowAction: {
    ...row('action'),
    marginTop: 16,
  },
  rowFeatures: {
    ...row('features'),
    marginTop: 16,
  },
  selected: {
    border: `1px solid ${theme.palette.brand.main}`,
  },
  category: {
    color: theme.palette.brand.main,
    fontSize: theme.brandTypography.text.small.fontSize,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  claim: {
    margin: 0,
    ...brandHeading(theme, 'h6', 'semibold'),
  },
  description: {
    margin: 0,
    fontSize: theme.brandTypography.text.small.fontSize,
  },
  price: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  },
  regularPrice: {
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.small.fontSize,
    textDecoration: 'line-through',
  },
  priceValue: {
    color: theme.palette.brand.main,
    ...brandHeading(theme, 'h3'),
  },
  priceUnit: {
    fontSize: theme.brandTypography.text.small.fontSize,
  },
  billingHint: {
    marginTop: 6,
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
  discount: {
    display: 'inline-flex',
    // a grid item would stretch over the whole width
    justifySelf: 'start',
    alignSelf: 'start',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    padding: '4px 8px',
    borderRadius: theme.borderRadius,
    background: theme.palette.brand.promotion,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
  button: {
    height: 48,
    ...brandText(theme, 'regular'),
  },
  primaryButton: {
    ...brandPrimaryButton(theme),
  },
  outlineButton: {
    ...brandOutlineButton(theme),
  },
  // a lower plan than the current one: no button, a hint instead
  locked: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    borderRadius: theme.borderRadiusLarge,
    background: theme.palette.brand.divider,
    color: theme.palette.brand.text.primary,
    '& svg': {
      flexShrink: 0,
      width: 20,
      height: 20,
      color: theme.palette.brand.text.secondary,
    },
  },
  lockedTitle: {
    display: 'block',
    ...brandText(theme, 'tiny', 'semibold'),
  },
  lockedReason: {
    display: 'block',
    color: theme.palette.brand.text.secondary,
    ...brandText(theme, 'tiny'),
  },
  featuresToggle: {
    alignSelf: 'flex-start',
    gap: 4,
    ...brandText(theme, 'small', 'medium'),
    '& svg': {
      transition: theme.transitions.create('transform'),
    },
  },
  featuresToggleOpen: {
    '& svg': {
      transform: 'rotate(180deg)',
    },
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.brand.divider}`,
    fontSize: theme.brandTypography.text.small.fontSize,
  },
  featuresTitle: {
    fontWeight: theme.brandTypography.weights.medium,
  },
  feature: {
    display: 'flex',
    gap: 8,
    '& svg': {
      flexShrink: 0,
      marginTop: 2,
    },
  },
}))

interface PlanFeature {
  label: string
  icon?: string
  html?: boolean
}

interface PlanCardProps {
  plan: ISubscriptionSelectionItem
  period: string
  status?: PlanStatus
  // an existing customer upgrades - "Upgrade wählen" instead of "Plan wählen"
  isUpgrade?: boolean
  // the current plan is the only way on (no higher plan) - its button is the primary one
  isCurrentPrimary?: boolean
  isSelected?: boolean
  showRecommended?: boolean
  onSelect: (plan: ISubscriptionSelectionItem) => void
  // "Mit diesem Plan weiter"
  onContinue?: () => void
}

const getPlanKey = (name: string) => name.replace(/_\d{4}$/, '')

const formatAmount = (value: number) => Number.isInteger(value)
  ? String(value)
  : formatCurrency(value, { symbol: '', format: '%v' })

const PlanCard = (props: PlanCardProps) => {
  const {
    plan,
    period,
    status = PLAN_STATUS.available,
    isUpgrade = false,
    isCurrentPrimary = false,
    isSelected = false,
    showRecommended = Boolean(plan.bestseller),
    onSelect,
    onContinue,
  } = props
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const isMobile = useMediaQuery(theme.breakpoints.down(768))
  const [showFeatures, setShowFeatures] = useState(false)
  const texts = lang.upgrade.wizard.plans
  const claim = texts.claims[getPlanKey(plan.name)]
  const isCurrent = status === PLAN_STATUS.current
  const isLocked = status === PLAN_STATUS.locked
  const hasBand = isCurrent || showRecommended
  const price = useMemo(() => getPlanPrice(plan, period), [plan, period])

  const features: PlanFeature[] = useMemo(() => {
    const { limits } = getPlanDescriptions(plan.name, plan.features || {})
    // the user count is part of the plan, not of its feature list
    return limits.filter((limit: PlanFeature) => limit.label !== lang.upgradeModal.plans.user)
  }, [plan])
  const [featuresTitle, ...featureItems] = features[0]?.icon === 'none' ? features : [null, ...features]

  // the label only - the explanations of the features are not shown (design)
  const renderFeature = (feature: PlanFeature) => (
    <div key={feature.label} className={classes.feature}>
      <CheckRoundedIcon style={{ color: theme.palette.brand.main }} />
      <span>{feature.label}</span>
    </div>
  )

  const renderAction = () => {
    if (isLocked) {
      return (
        <div className={classes.locked} data-qs-id={`checkout-plan-locked-${plan.name}`}>
          <LockOutlinedIcon />
          <span>
            <span className={classes.lockedTitle}>{texts.notAvailable}</span>
            <span className={classes.lockedReason}>{texts.notAvailableReason}</span>
          </span>
        </div>
      )
    }
    if (isCurrent) {
      return (
        <ButtonSquare
          className={clsx(classes.button, isCurrentPrimary ? classes.primaryButton : classes.outlineButton)}
          variant={isCurrentPrimary ? undefined : BUTTON_VARIANTS.outline}
          label={texts.continueWithCurrent}
          dataQsId={`checkout-plan-continue-${plan.name}`}
          onClick={onContinue}
        />
      )
    }
    return (
      <ButtonSquare
        className={clsx(classes.button, classes.primaryButton)}
        label={isUpgrade ? texts.upgrade : texts.choose}
        dataQsId={`checkout-plan-choose-${plan.name}`}
        onClick={() => onSelect(plan)}
      />
    )
  }

  const featureList = features.length > 0 && (
    <div className={classes.features}>
      {featuresTitle && (featuresTitle.html
        ? <div className={classes.featuresTitle} dangerouslySetInnerHTML={{ __html: featuresTitle.label }} />
        : <div className={classes.featuresTitle}>{featuresTitle.label}</div>)}
      {featureItems.map(renderFeature)}
    </div>
  )

  // phone: the features of a plan that cannot be booked are folded away
  const isFeatureListFolded = isLocked && isMobile

  const card = (
    <div className={clsx(classes.card, hasBand ? classes.cardInBand : classes.withoutBand, isSelected && classes.selected)}>
      <div className={clsx(classes.category, classes.rowCategory)}>{plan.title}</div>
      {claim && <h3 className={clsx(classes.claim, classes.rowClaim)}>{claim.claim}</h3>}
      {claim && <p className={clsx(classes.description, classes.rowDescription)}>{claim.description}</p>}
      {price && (
        <div className={classes.rowPrice}>
          <div className={classes.price}>
            {price.discountPercent && <span className={classes.regularPrice}>{`${formatAmount(price.regularPricePerMonth)} ${lang.currencyEuro}`}</span>}
            <span className={classes.priceValue} data-typography='heading' data-qs-id={`checkout-plan-price-${plan.name}`}>{formatAmount(price.pricePerMonth)}</span>
            <span className={classes.priceUnit}>{texts.perMonth}</span>
          </div>
          <div className={classes.billingHint}>{period === dateConstants.YEAR ? texts.billedYearly : texts.billedMonthly}</div>
        </div>
      )}
      {price?.discountPercent && (
        <div className={clsx(classes.discount, classes.rowDiscount)}>
          <SVGIcon svg={SellOutlineIcon} width={16} height={16} fill='currentColor' />
          {texts.discount(price.discountPercent, price.discountMonths)}
        </div>
      )}
      <div className={classes.rowAction}>{renderAction()}</div>
      {isFeatureListFolded && featureList
        ? (
          <div className={classes.rowFeatures}>
            <ButtonSquare
              variant={BUTTON_VARIANTS.link}
              className={clsx(classes.featuresToggle, showFeatures && classes.featuresToggleOpen)}
              aria-expanded={showFeatures}
              dataQsId={`checkout-plan-features-toggle-${plan.name}`}
              onClick={() => setShowFeatures(!showFeatures)}>
              {showFeatures ? texts.hideFeatures : texts.showFeatures}
              <ExpandMoreRoundedIcon />
            </ButtonSquare>
            <Collapse in={showFeatures} unmountOnExit>{featureList}</Collapse>
          </div>
        )
        : featureList && <div className={classes.rowFeatures}>{featureList}</div>}
    </div>
  )

  return (
    <div
      className={clsx(
        classes.root,
        hasBand && classes.withBand,
        isCurrent ? classes.current : (showRecommended && classes.recommended),
      )}
      data-qs-id={`checkout-plan-${plan.name}`}
      aria-current={isSelected || undefined}>
      {isCurrent && (
        <div className={clsx(classes.band, classes.bandCurrent)} data-qs-id={`checkout-plan-current-${plan.name}`}>
          <CheckCircleOutlineRoundedIcon />
          {texts.current}
        </div>
      )}
      {!isCurrent && showRecommended && (
        <div className={clsx(classes.band, classes.bandRecommended)}>
          <StarOutlineRoundedIcon />
          {texts.recommended}
        </div>
      )}
      {card}
    </div>
  )
}

export default PlanCard
